import { FormGroup } from '@angular/forms';
import { untilDestroyed } from '@ngneat/until-destroy';
import { isSameDay } from '@siat-fe/core';
import { tap } from 'rxjs';
import { computeMinTime, formatHHmm } from '../event-date-time-form.utils';

/**
 * Owns the reactive min-date/min-time wiring shared by the "add assenza" and
 * "add turno" dialogs: a date+time start/end range where the end can't be
 * before the start, and neither can be in the past. The form only needs to
 * carry `dateStart`/`timeStart`/`dateEnd`/`timeEnd` controls (it may carry
 * more, e.g. `collaboratore`/`ambulatorio`). `host` must be an instance of a
 * class decorated with `@UntilDestroy()`, so the subscriptions are torn down
 * with it.
 */
export class DateTimeRangeController {
  /*
   * Minimum selectable start date (can't be in the past).
   */
  minDateStart: Date = new Date();

  /*
   * Minimum selectable end date (can't be in the past).
   */
  minDateEnd: Date = new Date();

  /**
   * Minimum selectable start time ('HH:mm'), empty while no start date is chosen.
   */
  minTimeStart = '';

  /**
   * Minimum selectable end time ('HH:mm').
   */
  minTimeEnd = '00:00';

  constructor(
    private readonly form: FormGroup,
    host: unknown
  ) {
    this.form
      .get('dateStart')
      ?.valueChanges.pipe(
        untilDestroyed(host),
        tap((date: Date | null) => {
          this.minTimeStart = computeMinTime(date);
          this.minDateEnd = date ?? new Date();
          const dateEnd = this.form.get('dateEnd')?.value;
          if (dateEnd && date && dateEnd < date) {
            this.form.get('dateEnd')?.reset();
            this.form.get('timeEnd')?.reset();
          }
          this.updateMinTimeEnd();
        })
      )
      .subscribe();

    this.form
      .get('timeStart')
      ?.valueChanges.pipe(
        untilDestroyed(host),
        tap(() => this.updateMinTimeEnd())
      )
      .subscribe();

    this.form
      .get('dateEnd')
      ?.valueChanges.pipe(
        untilDestroyed(host),
        tap(() => this.updateMinTimeEnd())
      )
      .subscribe();

    // Un consumer può pre-compilare il form (es. prefill dallo slot cliccato
    // in calendario) prima che questo controller esista e si iscriva ai
    // valueChanges sopra: quell'emissione iniziale va persa, lasciando
    // minTimeStart/minDateEnd/minTimeEnd ai default basati su "adesso"
    // invece che sulla data pre-compilata, il che può marcare come invalido
    // un orario pre-compilato perfettamente valido (es. le 10:00 di domani,
    // rifiutate perché "adesso" sono le 15:30). Si risincronizza qui una
    // volta, leggendo il valore già presente nel form.
    const initialDateStart = this.form.get('dateStart')?.value;
    if (initialDateStart) {
      this.minTimeStart = computeMinTime(initialDateStart);
      this.minDateEnd = initialDateStart;
    }
    this.updateMinTimeEnd();
  }

  /**
   * Recomputes `minTimeEnd` from the current start date/time and end date,
   * resetting `timeEnd` if it's now below the new minimum.
   */
  private updateMinTimeEnd(): void {
    const dateStart = this.form.get('dateStart')?.value;
    const timeStart = this.form.get('timeStart')?.value;
    const dateEnd = this.form.get('dateEnd')?.value;

    if (dateStart && dateEnd && timeStart && isSameDay(dateStart, dateEnd)) {
      const minEnd = new Date(timeStart.getTime());
      minEnd.setMinutes(minEnd.getMinutes() + 15);
      this.minTimeEnd = formatHHmm(minEnd);
      const timeEnd = this.form.get('timeEnd')?.value;
      if (timeEnd && timeEnd <= timeStart) {
        this.form.get('timeEnd')?.reset();
      }
    } else {
      this.minTimeEnd = '00:00';
    }
  }
}
